// Touch / mobile-profile detection. Centralized so InputRouter, MobileActionBar,
// ArmedBanner, ClientApp, and the CSS-layout consumers all agree on a single
// rule: a "touch profile" client is one where the primary input is a coarse
// pointer (finger / stylus), OR the viewport is narrow enough that mobile
// layout should kick in regardless of input. Both predicates are evaluated
// once at startup (after the first user interaction the answer is reliable on
// every mainstream mobile browser) and cached — recomputing them per-frame is
// wasteful and gives jitter when the user resizes a desktop window across the
// threshold.
//
// Per-event branching uses `pointerEventIsTouch(...)` so a hybrid laptop with a
// touchscreen behaves correctly in both modes — finger taps take the touch
// path, mouse clicks take the desktop path. The cached `isTouchProfile()` is
// for layout decisions (CSS class, default zoom, on-screen action bar
// visibility) where flipping mode mid-match would be jarring.

// Narrow-viewport threshold matches the CSS breakpoint used across the UI
// stylesheets so behavior and layout flip together.
export const MOBILE_VIEWPORT_THRESHOLD_PX = 820;

let cached: boolean | null = null;
// One-shot flag so we only register the invalidation listeners once.
// Subsequent isTouchProfile() calls reuse the already-attached handlers.
let invalidationListenersInstalled = false;

// Invalidates the cached profile decision on a signal that could flip it:
//   - orientationchange — iPad portrait → landscape crosses the
//     narrow-viewport threshold even when the device is the same.
//   - resize — desktop window crossing 820 px, or iPad split-view.
//   - (pointer: coarse) media-query change — hybrid laptops where a
//     touchscreen / mouse is plugged in or unplugged mid-session.
//
// Previous behavior cached the answer forever after the first call, which
// meant an iPad opened in portrait at the menu and then rotated for
// gameplay would still get desktop-layout UI for the whole session —
// confusingly small touch targets, missing on-screen action bar. Not
// strictly a crash, but the UX inflates session-failure metrics on the
// CrazyGames portal because rotated-iPad players bounce.
function installInvalidationListeners(): void {
  if (invalidationListenersInstalled) return;
  if (typeof window === 'undefined') return;
  invalidationListenersInstalled = true;
  const invalidate = (): void => {
    cached = null;
  };
  window.addEventListener('resize', invalidate, { passive: true });
  window.addEventListener('orientationchange', invalidate, { passive: true });
  if (typeof window.matchMedia === 'function') {
    try {
      const mq = window.matchMedia('(pointer: coarse)');
      // Older Safari only supports the deprecated addListener; modern browsers
      // support addEventListener('change'). Feature-detect both — wrap in
      // try/catch so a hostile MediaQueryList implementation can't crash boot.
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', invalidate);
      } else if (typeof (mq as MediaQueryList & {
        addListener?: (l: () => void) => void;
      }).addListener === 'function') {
        (mq as MediaQueryList & { addListener: (l: () => void) => void }).addListener(invalidate);
      }
    } catch {
      // matchMedia change subscription is best-effort; the resize listener
      // catches the most common case (rotation) on its own.
    }
  }
}

export function isTouchProfile(): boolean {
  installInvalidationListeners();
  if (cached !== null) return cached;
  cached = detect();
  return cached;
}

// Test-only escape hatch: lets unit tests reset the cache between cases. Not
// used by production code paths.
export function __resetTouchProfileCache(): void {
  cached = null;
}

function detect(): boolean {
  if (typeof window === 'undefined') return false;
  const coarse =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  const narrow = window.innerWidth > 0 && window.innerWidth < MOBILE_VIEWPORT_THRESHOLD_PX;
  const hasTouchPoints =
    typeof navigator !== 'undefined' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 0;
  // Require BOTH a coarse-pointer signal AND touch capability OR a narrow
  // viewport. This avoids false positives on a desktop where the user has
  // (pointer: coarse) reported transiently by some browsers.
  return (coarse && hasTouchPoints) || narrow;
}

// Per-event predicate. Used by InputRouter to branch on individual pointer
// events without caching, since the same client can receive both mouse and
// touch events on a hybrid device.
export function pointerEventIsTouch(ev: { pointerType?: string } | null | undefined): boolean {
  if (ev === null || ev === undefined) return false;
  const t = ev.pointerType;
  return t === 'touch' || t === 'pen';
}

// Lightweight haptic feedback wrapper. Silent on devices without vibrate
// support. Kept short (10 ms) so it never feels intrusive; the goal is
// confirmatory feedback, not a buzz.
export function hapticTap(durationMs = 10): void {
  if (typeof navigator === 'undefined') return;
  const vibrate = (navigator as Navigator & { vibrate?: (p: number | number[]) => boolean })
    .vibrate;
  if (typeof vibrate !== 'function') return;
  try {
    vibrate.call(navigator, [durationMs]);
  } catch {
    // Some browsers throw if vibrate is called without a user gesture in
    // scope. Swallow — haptics are best-effort.
  }
}
